import * as THREE from "three";
import { BLOCK_SIZE } from "../config";
import { BLOCK, type BlockId, type VoxelWorld } from "../types";
import { BLOCK_FACE } from "./Materials";
import { SceneManager } from "./SceneManager";

const RENDERED_FLUIDS = [BLOCK.Water, BLOCK.Lava] as const;
const FLUID_SURFACE_OFFSET = BLOCK_SIZE * 0.35;

type RenderedFluid = (typeof RENDERED_FLUIDS)[number];
type Point = readonly [number, number, number];

interface GeometryBuffers {
    positions: number[];
    normals: number[];
    indices: number[];
    voxelCount: number;
}

const CORNER_NEIGHBORS = [
    (1 << 1) | (1 << 2),
    (1 << 0) | (1 << 3),
    (1 << 0) | (1 << 3),
    (1 << 1) | (1 << 2),
] as const;

export function isRenderedFluid(block: BlockId): block is RenderedFluid {
    return block === BLOCK.Water || block === BLOCK.Lava;
}

export function renderFluids(scene: SceneManager, world: VoxelWorld): void {
    const surfaces = buildSurfaceMaps(world);

    for (const fluid of RENDERED_FLUIDS) {
        const buffers = buildFluidGeometry(scene, world, fluid, surfaces[fluid]);
        if (buffers.indices.length === 0) continue;

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(buffers.positions, 3),
        );
        geometry.setAttribute(
            "normal",
            new THREE.Float32BufferAttribute(buffers.normals, 3),
        );
        geometry.setIndex(buffers.indices);
        geometry.computeBoundingSphere();

        const mesh = new THREE.Mesh(geometry, scene.materials[fluid].material);
        mesh.name = fluid === BLOCK.Water ? "terrain-water" : "terrain-lava";
        mesh.renderOrder = fluid === BLOCK.Water ? 2 : 1;
        mesh.userData.terrainOwnedGeometry = true;
        mesh.userData.voxelCount = buffers.voxelCount;
        scene.sharedGeometries.add(geometry);
        scene.group.add(mesh);
    }
}

function buildSurfaceMaps(
    world: VoxelWorld,
): Record<RenderedFluid, Int16Array> {
    const water = new Int16Array(world.size * world.size);
    const lava = new Int16Array(world.size * world.size);
    water.fill(-1);
    lava.fill(-1);

    for (let x = 0; x < world.size; x++) {
        for (let z = 0; z < world.size; z++) {
            const column = world.columnOffset(x, z);
            const index = x * world.size + z;
            for (let y = 0; y < world.height; y++) {
                const block = world.data[column + y] as BlockId;
                if (block === BLOCK.Water) water[index] = y;
                else if (block === BLOCK.Lava) lava[index] = y;
            }
        }
    }

    return {
        [BLOCK.Water]: water,
        [BLOCK.Lava]: lava,
    };
}

function buildFluidGeometry(
    scene: SceneManager,
    world: VoxelWorld,
    fluid: RenderedFluid,
    surface: Int16Array,
): GeometryBuffers {
    const positions: number[] = [];
    const normals: number[] = [];
    const indices: number[] = [];
    const halfWorld = (world.size * BLOCK_SIZE) / 2;
    let voxelCount = 0;

    const addQuad = (a: Point, b: Point, c: Point, d: Point, normal: Point) => {
        const base = positions.length / 3;
        positions.push(...a, ...b, ...c, ...d);
        normals.push(...normal, ...normal, ...normal, ...normal);
        indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    };

    for (let x = 0; x < world.size; x++) {
        for (let z = 0; z < world.size; z++) {
            const surfaceY = surface[x * world.size + z];
            if (surfaceY < 0) continue;

            const x0 = x * BLOCK_SIZE - halfWorld;
            const x1 = x0 + BLOCK_SIZE;
            const z0 = z * BLOCK_SIZE - halfWorld;
            const z1 = z0 + BLOCK_SIZE;

            for (let y = 0; y <= surfaceY; y++) {
                if (world.get(x, y, z) !== fluid) continue;
                voxelCount++;

                const isSurface = world.get(x, y + 1, z) !== fluid;
                const bottom = y * BLOCK_SIZE - BLOCK_SIZE / 2;
                const cellTop = y * BLOCK_SIZE + BLOCK_SIZE / 2;
                const top00 = isSurface
                    ? cornerHeight(surface, world.size, x, z, x, z, surfaceY)
                    : cellTop;
                const top10 = isSurface
                    ? cornerHeight(surface, world.size, x + 1, z, x, z, surfaceY)
                    : cellTop;
                const top11 = isSurface
                    ? cornerHeight(surface, world.size, x + 1, z + 1, x, z, surfaceY)
                    : cellTop;
                const top01 = isSurface
                    ? cornerHeight(surface, world.size, x, z + 1, x, z, surfaceY)
                    : cellTop;

                if (
                    isSurface &&
                    !coversBoundary(scene, world.get(x, y + 1, z), BLOCK_FACE.negativeY)
                ) {
                    addQuad(
                        [x0, top00, z0],
                        [x0, top01, z1],
                        [x1, top11, z1],
                        [x1, top10, z0],
                        [0, 1, 0],
                    );
                }

                if (
                    sideIsVisible(
                        scene,
                        world,
                        surface,
                        fluid,
                        x,
                        y,
                        z,
                        surfaceY,
                        -1,
                        0,
                        BLOCK_FACE.positiveX,
                    )
                ) {
                    addQuad(
                        [x0, bottom, z1],
                        [x0, top01, z1],
                        [x0, top00, z0],
                        [x0, bottom, z0],
                        [-1, 0, 0],
                    );
                }
                if (
                    sideIsVisible(
                        scene,
                        world,
                        surface,
                        fluid,
                        x,
                        y,
                        z,
                        surfaceY,
                        1,
                        0,
                        BLOCK_FACE.negativeX,
                    )
                ) {
                    addQuad(
                        [x1, bottom, z0],
                        [x1, top10, z0],
                        [x1, top11, z1],
                        [x1, bottom, z1],
                        [1, 0, 0],
                    );
                }
                if (
                    sideIsVisible(
                        scene,
                        world,
                        surface,
                        fluid,
                        x,
                        y,
                        z,
                        surfaceY,
                        0,
                        -1,
                        BLOCK_FACE.positiveZ,
                    )
                ) {
                    addQuad(
                        [x0, bottom, z0],
                        [x0, top00, z0],
                        [x1, top10, z0],
                        [x1, bottom, z0],
                        [0, 0, -1],
                    );
                }
                if (
                    sideIsVisible(
                        scene,
                        world,
                        surface,
                        fluid,
                        x,
                        y,
                        z,
                        surfaceY,
                        0,
                        1,
                        BLOCK_FACE.negativeZ,
                    )
                ) {
                    addQuad(
                        [x1, bottom, z1],
                        [x1, top11, z1],
                        [x0, top01, z1],
                        [x0, bottom, z1],
                        [0, 0, 1],
                    );
                }

                if (
                    world.get(x, y - 1, z) !== fluid &&
                    !coversBoundary(scene, world.get(x, y - 1, z), BLOCK_FACE.positiveY)
                ) {
                    addQuad(
                        [x0, bottom, z0],
                        [x1, bottom, z0],
                        [x1, bottom, z1],
                        [x0, bottom, z1],
                        [0, -1, 0],
                    );
                }
            }
        }
    }

    return { positions, normals, indices, voxelCount };
}

function sideIsVisible(
    scene: SceneManager,
    world: VoxelWorld,
    surface: Int16Array,
    fluid: RenderedFluid,
    x: number,
    y: number,
    z: number,
    surfaceY: number,
    dx: number,
    dz: number,
    neighborFace: number,
): boolean {
    const neighbor = world.get(x + dx, y, z + dz);
    if (neighbor === fluid) return false;

    const neighborSurface = surfaceAt(surface, world.size, x + dx, z + dz);
    if (y === surfaceY && neighborSurface >= 0 && Math.abs(neighborSurface - surfaceY) <= 1) {
        return false;
    }

    return !coversBoundary(scene, neighbor, neighborFace);
}

function coversBoundary(scene: SceneManager, block: BlockId, face: number): boolean {
    if (block === BLOCK.Empty || isRenderedFluid(block)) return false;
    const entry = scene.materials[block];
    return Boolean(entry && (entry.boundaryFaces & face) !== 0);
}

function cornerHeight(
    surface: Int16Array,
    size: number,
    cornerX: number,
    cornerZ: number,
    ownX: number,
    ownZ: number,
    ownLevel: number,
): number {
    const levels = [
        surfaceAt(surface, size, cornerX - 1, cornerZ - 1),
        surfaceAt(surface, size, cornerX, cornerZ - 1),
        surfaceAt(surface, size, cornerX - 1, cornerZ),
        surfaceAt(surface, size, cornerX, cornerZ),
    ];
    let activeMask = 0;
    for (let index = 0; index < levels.length; index++) {
        if (levels[index] >= 0) activeMask |= 1 << index;
    }
    const ownIndex = (ownX === cornerX ? 1 : 0) + (ownZ === cornerZ ? 2 : 0);

    let connectedMask = 1 << ownIndex;
    let frontier = connectedMask;
    while (frontier !== 0) {
        let neighbors = 0;
        for (let index = 0; index < levels.length; index++) {
            if ((frontier & (1 << index)) !== 0) neighbors |= CORNER_NEIGHBORS[index];
        }
        const next = neighbors & activeMask & ~connectedMask;
        connectedMask |= next;
        frontier = next;
    }

    let total = 0;
    let count = 0;
    for (let index = 0; index < levels.length; index++) {
        if ((connectedMask & (1 << index)) === 0) continue;
        total += levels[index];
        count++;
    }
    const average = count > 0 ? total / count : ownLevel;
    return average * BLOCK_SIZE + FLUID_SURFACE_OFFSET;
}

function surfaceAt(surface: Int16Array, size: number, x: number, z: number): number {
    if (x < 0 || z < 0 || x >= size || z >= size) return -1;
    return surface[x * size + z];
}
